import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Users, Info } from "lucide-react";
import { Button, Input, Modal, ModalFooter, Select } from "@/components/ui";
import { PhoneInput } from "@/components/ui/phone";
import { RegionSelect } from "@/components/common/RegionSelect";
import { resolveDefaultPhoneCountry } from "@/lib/phoneDefaults";
import { isValidPhone } from "@/lib/phoneValidation";
import {
  StaffRolePreset,
  type StaffRolePreset as StaffRolePresetValue,
} from "@/services/staffAccessService";

const HIREABLE_PRESETS: StaffRolePresetValue[] = [
  StaffRolePreset.DEVICE_TECH,
  StaffRolePreset.DATA_ENTRY,
  StaffRolePreset.ACCOUNTING,
  StaffRolePreset.USER_MANAGEMENT,
];

export interface CreateStaffUserModalLabels {
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequired?: string;
  regionLabel: string;
  regionPlaceholder?: string;
  regionRequired?: string;
  presetLabel: string;
  choosePreset?: string;
  presetRequired?: string;
  inviteEmailHint: string;
  cancel: string;
  create: string;
}

interface CreateStaffUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    email: string;
    region_id: string;
    contact_phone: string;
    preset_key: StaffRolePresetValue;
  }) => void;
  isLoading?: boolean;
  labels: CreateStaffUserModalLabels;
}

export function CreateStaffUserModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  labels,
}: CreateStaffUserModalProps) {
  const { t, i18n } = useTranslation("common");
  const { t: tAdmin } = useTranslation("adminUsers");
  const { t: tValidation } = useTranslation("validation");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [regionId, setRegionId] = useState("");
  const [presetKey, setPresetKey] = useState<StaffRolePresetValue | "">("");
  const [regionError, setRegionError] = useState<string | undefined>();
  const [presetError, setPresetError] = useState<string | undefined>();
  const [phoneError, setPhoneError] = useState<string | undefined>();

  const defaultCountry = resolveDefaultPhoneCountry({ language: i18n.language });

  const presetOptions = useMemo(
    () =>
      HIREABLE_PRESETS.map((key) => ({
        value: key,
        label: tAdmin(`staffAccess.presets.${key}`),
      })),
    [tAdmin],
  );

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setEmail("");
      setPhone("");
      setRegionId("");
      setPresetKey("");
      setRegionError(undefined);
      setPresetError(undefined);
      setPhoneError(undefined);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let valid = true;
    if (!regionId) {
      setRegionError(labels.regionRequired);
      valid = false;
    } else {
      setRegionError(undefined);
    }
    if (!presetKey) {
      setPresetError(labels.presetRequired);
      valid = false;
    } else {
      setPresetError(undefined);
    }
    if (!phone.trim()) {
      setPhoneError(labels.phoneRequired);
      valid = false;
    } else if (!isValidPhone(phone)) {
      setPhoneError(tValidation("invalidPhoneFormat"));
      valid = false;
    } else {
      setPhoneError(undefined);
    }
    if (!valid || !presetKey) return;
    onSubmit({
      name: name.trim().replace(/\s+/g, " "),
      email: email.trim(),
      region_id: regionId,
      contact_phone: phone.trim(),
      preset_key: presetKey,
    });
  };

  return (
    <Modal
      closeLabel={t("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={labels.title}
      size="md"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {labels.cancel}
          </Button>
          <Button type="submit" form="create-staff-user-form" isLoading={isLoading}>
            {labels.create}
          </Button>
        </ModalFooter>
      }
    >
      <form
        id="create-staff-user-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <RegionSelect
          value={regionId}
          onChange={(value) => {
            setRegionId(value);
            if (regionError) setRegionError(undefined);
          }}
          label={labels.regionLabel}
          placeholder={labels.regionPlaceholder}
          error={regionError}
          required
        />

        <Select
          label={labels.presetLabel}
          value={presetKey}
          onChange={(e) => {
            setPresetKey(e.target.value as StaffRolePresetValue | "");
            if (presetError) setPresetError(undefined);
          }}
          options={presetOptions}
          placeholder={labels.choosePreset}
          error={presetError}
          required
        />

        <Input
          label={labels.nameLabel}
          placeholder={labels.namePlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          leftIcon={<Users className="h-4 w-4" />}
          required
        />

        <Input
          type="email"
          label={labels.emailLabel}
          placeholder={labels.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leftIcon={<Mail className="h-4 w-4" />}
          required
          autoComplete="off"
        />

        <PhoneInput
          label={labels.phoneLabel}
          placeholder={labels.phonePlaceholder}
          value={phone}
          onChange={(value) => {
            setPhone(value);
            if (phoneError) setPhoneError(undefined);
          }}
          defaultCountry={defaultCountry}
          error={phoneError}
          required
          autoComplete="off"
        />

        <p className="flex items-start gap-2 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2.5 text-[13px] leading-relaxed text-surface-600">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-surface-400" aria-hidden />
          {labels.inviteEmailHint}
        </p>
      </form>
    </Modal>
  );
}
